import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { Project } from '../types';
import { ApiService } from '../services/api';

interface ProjectContextType {
  selectedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
  projectList: Project[];
  setProjectList: (projects: Project[]) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectList, setProjectList] = useState<Project[]>([]);

  // Initialize from localStorage on mount
  useEffect(() => {
    try {
      const storedProject = localStorage.getItem('currentProject');
      const storedProjectId = localStorage.getItem('currentProjectId');
      if (!selectedProject && storedProject) {
        const parsed: Project = JSON.parse(storedProject);
        setSelectedProject(parsed);
      } else if (!selectedProject && storedProjectId) {
        // If only ID exists, at least ensure ApiService knows it
        ApiService.setCurrentProjectId(Number(storedProjectId));
      }
    } catch (e) {
      // ignore JSON parse/storage errors
    }
  }, []);

  // Persist and sync to ApiService whenever selection changes
  useEffect(() => {
    try {
      if (selectedProject && selectedProject.id != null) {
        localStorage.setItem('currentProject', JSON.stringify(selectedProject));
        localStorage.setItem('currentProjectId', String(selectedProject.id));
        ApiService.setCurrentProjectId(selectedProject.id);
      } else {
        localStorage.removeItem('currentProject');
        localStorage.removeItem('currentProjectId');
        ApiService.setCurrentProjectId(undefined);
      }
    } catch (e) {
      // ignore storage errors
    }
  }, [selectedProject]);

  return (
    <ProjectContext.Provider value={{ selectedProject, setSelectedProject, projectList, setProjectList }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};
